// ---------------------------------------------------------------------------
// Editor Store — combines all slices into a single Zustand store with Immer.
// ---------------------------------------------------------------------------

import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { createCanvasSlice, type CanvasSlice } from './canvas-slice'
import { createSelectionSlice, type SelectionSlice } from './selection-slice'
import { createUiSlice, type UiSlice } from './page-ui-slice'
import { createPluginSlice, type PluginSlice } from './plugin-slice'
import { createClipboardSlice, type ClipboardSlice } from './clipboard-slice'
import { mcpBus } from '@/mcp/studio-mcp-bus'

export type EditorState = CanvasSlice &
  SelectionSlice &
  UiSlice &
  PluginSlice &
  ClipboardSlice & {
    canUndo: boolean
    canRedo: boolean
    undo: () => void
    redo: () => void
    reset: () => void
  }

export const useEditorStore = create<EditorState>()(
  immer((set, get, api) => ({
    ...createCanvasSlice(set as never, get as never, api),
    ...createSelectionSlice(set as never, get as never, api),
    ...createUiSlice(set as never, get as never, api),
    ...createPluginSlice(set as never, get as never, api),
    ...createClipboardSlice(set as never, get as never, api),

    // History — toolbar reads from useUndoStateStore directly; these are for type contract only
    canUndo: false,
    canRedo: false,
    undo: () => { mcpBus.execute({ tool: 'cogo.studio.undo', params: {} }) },
    redo: () => { mcpBus.execute({ tool: 'cogo.studio.redo', params: {} }) },

    reset: () => {
      set((state) => {
        // Canvas
        state.app = null
        state.blocks = {}
        state.data = {}
        state.flows = {}
        state.tokens = null
        state.activePageId = null
        // Selection
        state.selectedIds = []
        state.selectionTier = 'component'
        state.selectedBlockName = null
        state.selectedPageId = null
        // History
        state.canUndo = false
        state.canRedo = false
        // UI
        state.activeBreakpoint = 'desktop'
        state.appTarget = 'web'
        state.isPreview = false
        state.leftPanelOpen = true
        state.rightPanelOpen = true
        state.zoom = 1
        state.panX = 0
        state.panY = 0
        // Clipboard
        state.clipboard = { elements: [], sourceBlockName: null, operation: null }
        // Plugins
        state.plugins = {}
      })
    },
  })),
)

// Expose the page-editor source of truth only in mock E2E runs so browser tests
// can assert Page design edits against the Coogo JSON state, not just DOM paint.
if (typeof window !== 'undefined' && import.meta.env.VITE_E2E_MOCK === 'true') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ;(window as any).__COGO_EDITOR__ = useEditorStore
}
